
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { TeamUser, UserRole } from "./UserListItem";

export const useUsersManagement = () => {
  const [users, setUsers] = useState<TeamUser[]>([
    {
      id: "1",
      name: "Admin User",
      email: "admin@example.com",
      role: "admin"
    },
    {
      id: "2",
      name: "Maria Silva",
      email: "maria@example.com",
      role: "editor"
    }
  ]);
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TeamUser | null>(null);
  
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newUserRole, setNewUserRole] = useState<UserRole>("viewer");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { toast } = useToast();

  const handleAddUser = async () => {
    if (!newUserEmail || !newUserName) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // In a real implementation, this would invite the user via Supabase
      // For now, we'll just add them to our local state
      const newUser: TeamUser = {
        id: `user-${Date.now()}`,
        name: newUserName,
        email: newUserEmail,
        role: newUserRole
      };
      
      setUsers([...users, newUser]);
      
      toast({
        title: "Usuário adicionado",
        description: `${newUserName} foi adicionado como ${newUserRole}`,
      });
      
      setIsAddDialogOpen(false);
      resetForm();
      
    } catch (error: any) {
      console.error("Error adding user:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível adicionar o usuário",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleEditUser = (user: TeamUser) => {
    setSelectedUser(user);
    setNewUserName(user.name);
    setNewUserEmail(user.email);
    setNewUserRole(user.role);
    setIsEditDialogOpen(true);
  };
  
  const saveUserChanges = () => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    
    try {
      // Update the user in our local state
      const updatedUsers = users.map(user => 
        user.id === selectedUser.id 
          ? { ...user, name: newUserName, role: newUserRole } 
          : user
      );
      
      setUsers(updatedUsers);
      
      toast({
        title: "Usuário atualizado",
        description: `${newUserName} foi atualizado com sucesso`,
      });
      
      setIsEditDialogOpen(false);
      resetForm();
      
    } catch (error: any) {
      console.error("Error updating user:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível atualizar o usuário",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const resetForm = () => {
    setNewUserEmail("");
    setNewUserName("");
    setNewUserRole("viewer");
    setSelectedUser(null);
  };
  
  // Function to refresh the users list (for when a user is deleted)
  const refreshUsers = () => {
    // In a real app, this would fetch users from the database
    // For now, we'll just simulate by removing any users that might have been deleted
    // In the future, this would be replaced with an actual API call
    console.log("Refreshing users list");
  };

  return {
    users,
    isAddDialogOpen,
    setIsAddDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    selectedUser,
    newUserEmail,
    setNewUserEmail,
    newUserName,
    setNewUserName,
    newUserRole,
    setNewUserRole,
    isSubmitting,
    handleAddUser,
    handleEditUser,
    saveUserChanges,
    refreshUsers
  };
};
